import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { styled } from '@linaria/react';
import { AnimatePresence, motion } from 'framer-motion';
import noop from 'lodash/noop';
import { toggle } from 'opticks';
import { useOnClickOutside } from '@daedalus/atlas/hooks/useOnClickOutside';
import { fadeInOutAndHide, transitions } from '@daedalus/atlas/utils/framerTransitions';
import { Action, AnalyticsContext, Category, Entity, TrackEventPayload } from '@daedalus/core/src/analytics/types/Events';
import { shouldCookieBannerBeShown } from '.';
import { CookieConsentBanner } from './CookieConsentBanner';
import { CookieBannerClickComponent, useCookieConsent } from './useCookieConsent';
export const osanoCustomStyles = "ou6uzw7";
const _exp18 = /*#__PURE__*/() => motion.div;
const CookieBannerWrapper = /*#__PURE__*/styled(_exp18())({
  name: "CookieBannerWrapper",
  class: "c16g77j2",
  propsAsIs: true
});
export interface CookieBannerProps {
  /**
   * Callback invoked when a user accepts the cookies, either in the
   * banner or the preference center
   */
  onAccept?: (acceptedByUser: boolean) => void;
  trackEvent: (payload: TrackEventPayload) => void;
  brandIsVio?: boolean;
}
export const CookieConsentBannerContainer = ({
  onAccept: setCookiesAcceptedByUser = noop,
  brandIsVio = false,
  trackEvent
}: CookieBannerProps) => {
  const {
    locale
  } = useIntl();
  const ref = useRef(null);
  const [isCookieBannerVisible, setIsCookieBannerVisible] = useState(shouldCookieBannerBeShown);
  const showOsanoCookieBanner = isCookieBannerVisible && brandIsVio && toggle('c00k1eba-replace-custom-cookie-banner', false, true);
  const onAcceptCookies = useCallback(() => {
    setIsCookieBannerVisible(false);
    setCookiesAcceptedByUser(true);
  }, [setIsCookieBannerVisible, setCookiesAcceptedByUser]);
  const {
    trackBannerSubmitted,
    acceptAll,
    denyAll,
    openPreferencesCenter,
    cookieConsentContext,
    isPreferencesCenterVisible
  } = useCookieConsent(onAcceptCookies, trackEvent);
  const handleAcceptAllCookies = useCallback(() => {
    acceptAll();
    trackBannerSubmitted(CookieBannerClickComponent.AcceptButton);
  }, [acceptAll, setCookiesAcceptedByUser, trackBannerSubmitted]);
  const handleRejectAllCookies = useCallback((component?: CookieBannerClickComponent, preventTracking = false) => {
    if (isCookieBannerVisible) {
      denyAll();
      setIsCookieBannerVisible(false);
      setCookiesAcceptedByUser(false);
      if (!preventTracking) trackBannerSubmitted(component);
    }
  }, [isCookieBannerVisible, denyAll, setCookiesAcceptedByUser, trackBannerSubmitted]);
  const trackBannerShown = () => {
    if (!isCookieBannerVisible) return;
    trackEvent({
      category: Category.System,
      entity: Entity.CookieBanner,
      action: Action.Displayed,
      analyticsContext: {
        [AnalyticsContext.CookieConsentContext]: cookieConsentContext
      }
    });
  };
  const trackOsanoConsentSaved = useCallback(consent => {
    const component = consent?.ANALYTICS === 'ACCEPT' || consent?.PERSONALIZATION === 'ACCEPT' || consent?.MARKETING === 'ACCEPT' ? CookieBannerClickComponent.AcceptButton : CookieBannerClickComponent.RejectButton;
    trackBannerSubmitted(component);
  }, [trackBannerSubmitted]);
  useOnClickOutside(ref, event => {
    const target = event?.target as HTMLElement;
    const isOsanoDialog = target?.closest('.osano-cm-dialog');
    if (
    // If the user clicks outside the cookie banner, we need to reject all cookies
    // unless the user is in the preferences center.
    !isPreferencesCenterVisible &&
    // Because Osano's cookie banner is rendered in a different DOM tree
    // we need to check if the user clicked inside the cookie banner to not reject cookies by mistake
    !isOsanoDialog) {
      handleRejectAllCookies(CookieBannerClickComponent.AppPage, showOsanoCookieBanner);
    }
  });
  useEffect(trackBannerShown, [isCookieBannerVisible, trackEvent]);
  useEffect(() => {
    const sanitizedLocale = locale.split('-')[0];
    if (window.Osano?.cm) {
      window.Osano.cm.locale = sanitizedLocale;
    }
  }, [locale]);
  useEffect(() => {
    if (showOsanoCookieBanner) {
      const osanoDialog = document?.querySelector('.osano-cm-dialog') as HTMLElement;
      if (osanoDialog) osanoDialog.style.display = 'flex';

      // track the consent saved event from Osano when the user clicks on the accept/reject buttons
      window.Osano?.cm?.addEventListener('osano-cm-consent-saved', trackOsanoConsentSaved);
    }
  }, [showOsanoCookieBanner, trackOsanoConsentSaved]);
  return <AnimatePresence>
      <div ref={ref}>
        {!showOsanoCookieBanner && isCookieBannerVisible && <CookieBannerWrapper key="CookieBannerWrapper" initial="initial" animate="animate" exit="exit" variants={fadeInOutAndHide} transition={transitions.easeInOut}>
            <CookieConsentBanner openPreferencesCenter={openPreferencesCenter} acceptAllCookies={handleAcceptAllCookies} rejectAllCookies={handleRejectAllCookies} />
          </CookieBannerWrapper>}
      </div>
    </AnimatePresence>;
};

require("./CookieConsentBannerContainer.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./CookieConsentBannerContainer.tsx");